import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BadgeCheck, Crown, Plug, Sparkles } from "lucide-react";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { listUsers, disableUser, enableUser, deleteUser, AdminUserItem } from "@/services/adminApi";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { formatRelativeFr, pluginIconColor, premiumLabel, seniorityLabel } from "@/lib/pluginStatusBadge";
import { resolveTierBadge } from "@/lib/tierBadge";
import { roleBadgeLabel } from "@/lib/roleBadge";

export default function UsersPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<AdminUserItem[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listUsers(navigate);
      setItems(data.items || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDisable = async (userId: string) => {
    const ok = await confirmDialog({
      title: "Désactiver le compte",
      description: "L'utilisateur ne pourra plus se connecter tant que le compte est désactivé.",
      confirmLabel: "Désactiver",
      destructive: true,
    });
    if (!ok) return;
    await disableUser(userId, navigate);
    fetchData();
  };

  const onEnable = async (userId: string) => {
    const ok = await confirmDialog({
      title: "Activer le compte",
      description: "L'utilisateur pourra à nouveau se connecter.",
      confirmLabel: "Activer",
    });
    if (!ok) return;
    await enableUser(userId, navigate);
    fetchData();
  };

  const onDelete = async (u: AdminUserItem) => {
    const email = u.profile?.email || null;
    const isOrg = !!u.organization;
    // Saisie de confirmation : l'email du compte (visible dans la ligne), ou un
    // mot-clé de repli si l'email est inconnu.
    const requireText = email ?? "SUPPRIMER";
    const ok = await confirmDialog({
      title: "Supprimer définitivement le compte",
      description: isOrg
        ? `Ce membre sera retiré de l'organisation « ${u.organization?.name} » et un siège sera libéré (facturation ajustée au prorata). Ses données personnelles et son identité seront définitivement supprimées. Action irréversible.`
        : "L'abonnement Stripe actif sera annulé, puis le compte, ses candidats, campagnes et messages seront définitivement supprimés (RGPD). Action irréversible.",
      confirmLabel: "Supprimer définitivement",
      destructive: true,
      requireText,
      requireTextLabel: email ? (
        <>
          Pour confirmer, saisissez l'email du compte&nbsp;:{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">{email}</code>
        </>
      ) : undefined,
    });
    if (!ok) return;
    try {
      const summary = await deleteUser(u.user_id, navigate);
      const desc =
        summary?.branch === "b2b"
          ? `Membre retiré de l'organisation${summary.org_seat_freed ? " — 1 siège libéré" : ""}.`
          : summary?.stripe_subscriptions_cancelled?.length
            ? "Abonnement Stripe annulé et compte supprimé."
            : "Compte supprimé.";
      toast({ title: "Compte supprimé", description: desc });
      fetchData();
    } catch (e) {
      toast({
        title: "Échec de la suppression",
        description: e instanceof Error ? e.message : "Erreur inconnue",
        variant: "destructive",
      });
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Utilisateurs</h1>
          </div>
        </header>

        <div className="p-4">
          {loading && <div>Chargement…</div>}
          {error && <div className="text-red-600 text-sm">{error}</div>}

          {!loading && !error && (
            <TooltipProvider delayDuration={150}>
              <div className="overflow-x-auto rounded-md border">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-3">Utilisateur</th>
                      <th className="p-3">Email</th>
                      <th className="p-3">Inscription</th>
                      <th className="p-3">Abonnement</th>
                      <th className="p-3">Plugin</th>
                      <th className="p-3">Statut</th>
                      <th className="p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((u) => {
                      const enabled = u.profile?.enabled !== false; // par défaut true si indéterminé
                      const date = u.registration_date ? new Date(u.registration_date).toLocaleString() : "-";
                      const isConnected = !!u.plugin?.is_connected;
                      const lastSeen = u.plugin?.last_seen ?? null;
                      const version = u.plugin?.version ?? null;
                      const verified = u.linkedin?.profile_verified === true;
                      const premium = u.linkedin?.premium === true;
                      // Badge "Ancien" : reflete uniquement le critere
                      // `linkedin_profile_age` (compte LinkedIn >= 7 ans).
                      // `attached_90d` reste en page de detail (UserDetailPage).
                      const isAncient = u.account_tier_reason === "linkedin_profile_age";
                      // Badge tier : B2B (org) prime sur le subscription_type B2C.
                      const tierBadge = resolveTierBadge({
                        is_business: u.organization?.is_business,
                        organization_tier: u.organization?.tier,
                        subscription_type: u.subscription_type,
                      });
                      return (
                        <tr key={u.user_id} className="border-t">
                          <td className="p-3 font-medium">
                            <Link
                              to={`/users/${u.user_id}`}
                              className="text-foreground hover:text-primary hover:underline"
                            >
                              {u.profile?.first_name || ""} {u.profile?.last_name || ""}
                            </Link>
                          </td>
                          <td className="p-3">{u.profile?.email || "-"}</td>
                          <td className="p-3">{date}</td>
                          <td className="p-3">
                            <div className="flex flex-col items-start gap-0.5">
                              {tierBadge ? (
                                <Badge
                                  variant={tierBadge.variant}
                                  className={cn("uppercase", tierBadge.className)}
                                >
                                  {tierBadge.label}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                              {u.organization?.member_role && (
                                <span className="text-xs text-muted-foreground">
                                  {roleBadgeLabel(u.organization.member_role)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Plug className={`h-4 w-4 ${pluginIconColor(isConnected, lastSeen)}`} aria-label="Statut du plugin" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[240px] text-xs">
                                  <p>
                                    Plugin {isConnected ? "en ligne" : "hors ligne"} — {formatRelativeFr(lastSeen)}
                                    {version ? ` · v${version}` : ""}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                              {verified && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <BadgeCheck className="h-4 w-4 text-emerald-600" aria-label="Profil LinkedIn vérifié" />
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">Profil LinkedIn vérifié</TooltipContent>
                                </Tooltip>
                              )}
                              {premium && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Crown className="h-4 w-4 text-amber-600" aria-label="Compte LinkedIn Premium" />
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    {premiumLabel(u.linkedin?.premium_type)}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {isAncient && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Sparkles className="h-4 w-4 text-violet-600" aria-label="Profil LinkedIn ancien" />
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    Profil ancien — {seniorityLabel(u.linkedin?.profile_oldest_year)}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs ${enabled ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-700"}`}>
                              {enabled ? "Activé" : "Désactivé"}
                            </span>
                          </td>
                          <td className="p-3 space-x-2">
                            {enabled ? (
                              <button className="text-xs px-2 py-1 rounded bg-yellow-100 hover:bg-yellow-200" onClick={() => onDisable(u.user_id)}>
                                Désactiver
                              </button>
                            ) : (
                              <button className="text-xs px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200" onClick={() => onEnable(u.user_id)}>
                                Activer
                              </button>
                            )}
                            <button className="text-xs px-2 py-1 rounded bg-red-100 hover:bg-red-200" onClick={() => onDelete(u)}>
                              Supprimer
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </TooltipProvider>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
